import { Loader2, Terminal, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { JSX } from "react";
import { ThemedText, ThemedIcon, ThemedCard } from "@/components/primitives";
import { ThemeProps } from "@/lib/theme";
import { Grid } from "@/components/layouts";

interface LoadingStateProps extends ThemeProps {
  variant?: "default" | "terminal" | "konami";
  message?: string;
  size?: "sm" | "md" | "lg";
}

export const LoadingState = ({
  variant = "default",
  message = "Loading...",
  size = "md",
  konamiMode = false,
}: LoadingStateProps): JSX.Element => {
  const getIcon = (): JSX.Element => {
    const iconProps = {
      konamiMode: konamiMode || variant === "konami",
      size: size as "sm" | "md" | "lg",
      className: variant === "konami" || konamiMode ? "animate-pulse" : "animate-spin",
    };

    switch (variant) {
      case "terminal":
        return (
          <ThemedIcon {...iconProps}>
            <Terminal className="h-full w-full animate-pulse" />
          </ThemedIcon>
        );
      case "konami":
        return (
          <ThemedIcon {...iconProps}>
            <Zap className="h-full w-full animate-pulse" />
          </ThemedIcon>
        );
      default:
        return (
          <ThemedIcon {...iconProps}>
            <Loader2 className="h-full w-full animate-spin" />
          </ThemedIcon>
        );
    }
  };

  const containerGap = {
    sm: "gap-2",
    md: "gap-3",
    lg: "gap-4",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`flex items-center justify-center ${containerGap[size]}`}
    >
      {getIcon()}
      <ThemedText
        konamiMode={konamiMode || variant === "konami"}
        variant="primary"
        size={size === "sm" ? "sm" : size === "lg" ? "lg" : "base"}
        className="font-mono"
      >
        {message}
      </ThemedText>
    </motion.div>
  );
};

export const PageLoadingState = ({ konamiMode = false }: ThemeProps): JSX.Element => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <LoadingState
      konamiMode={konamiMode}
      variant={konamiMode ? "konami" : "terminal"}
      message={konamiMode ? "Initializing TRON mode..." : "Loading terminal..."}
      size="lg"
    />
  </div>
);

export const InlineLoadingState = ({
  message = "Loading...",
}: {
  message?: string;
}): JSX.Element => <LoadingState variant="default" message={message} size="sm" />;

export const SkeletonCard = ({ konamiMode = false }: ThemeProps): JSX.Element => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
    <ThemedCard konamiMode={konamiMode} variant="base" className="animate-pulse">
      <div className="space-y-3">
        <div
          className={`h-4 w-3/4 rounded ${konamiMode ? "bg-[#00FFFF]/20" : "bg-green-400/20"}`}
        ></div>
        <div
          className={`h-3 w-1/2 rounded ${konamiMode ? "bg-[#00FFFF]/20" : "bg-green-400/20"}`}
        ></div>
        <div className="space-y-2">
          <div
            className={`h-2 rounded ${konamiMode ? "bg-[#00FFFF]/20" : "bg-green-400/20"}`}
          ></div>
          <div
            className={`h-2 w-5/6 rounded ${konamiMode ? "bg-[#00FFFF]/20" : "bg-green-400/20"}`}
          ></div>
        </div>
      </div>
    </ThemedCard>
  </motion.div>
);

export const SkeletonGrid = ({
  count = 6,
  konamiMode = false,
}: { count?: number } & ThemeProps): JSX.Element => (
  <Grid cols={1} mdCols={2} lgCols={3} gap="md">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} konamiMode={konamiMode} />
    ))}
  </Grid>
);

